import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSSEStream } from '../../../hooks/useSSEStream';
import { whatsappApi } from '../api/whatsappApi';
import type { SendMessageInput, WhatsAppStreamEvent } from '../types';

/**
 * How often an open conversation re-checks for messages.
 *
 * Ten seconds is short enough that a reply is never "late" in a conversation
 * people are actively having, and long enough that an idle open tab is not a
 * load problem. It only runs while the screen is focused.
 */
const MESSAGE_POLL_MS = 10_000;

const whatsappKeys = {
  all: ['whatsapp'] as const,
  conversations: () => [...whatsappKeys.all, 'conversations'] as const,
  conversationList: (orgId: string) => [...whatsappKeys.conversations(), orgId] as const,
  messages: (conversationId: string, orgId: string) =>
    [...whatsappKeys.all, 'messages', orgId, conversationId] as const,
  window: (conversationId: string, orgId: string) =>
    [...whatsappKeys.all, 'window', orgId, conversationId] as const,
  templates: (orgId: string) => [...whatsappKeys.all, 'templates', orgId] as const,
};

export function useWhatsAppConversations(organizationId: string | undefined) {
  return useInfiniteQuery({
    queryKey: whatsappKeys.conversationList(organizationId ?? ''),
    queryFn: ({ pageParam }) =>
      whatsappApi.getConversations({
        organizationId: organizationId ?? '',
        limit: 20,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useWhatsAppMessages(
  conversationId: string | undefined,
  organizationId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: whatsappKeys.messages(conversationId ?? '', organizationId ?? ''),
    queryFn: ({ pageParam }) =>
      whatsappApi.getMessages(conversationId ?? '', organizationId ?? '', {
        limit: 50,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Without the organization the request is rejected by the plan guard before
    // it reaches a handler, so waiting for it beats a guaranteed 400.
    enabled: !!conversationId && !!organizationId,
    // A floor under the SSE stream, not a replacement for it. The stream is
    // what makes a reply appear instantly; this is what keeps an open
    // conversation from going stale when the stream drops, which is invisible
    // to the reader — they just see a conversation that stopped moving while
    // the other person is still writing.
    refetchInterval: MESSAGE_POLL_MS,
    refetchIntervalInBackground: false,
    // The poll is pointless if a cached page is served instead.
    staleTime: 0,
  });
}

export function useSendWhatsAppMessage(conversationId: string, organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageInput) =>
      whatsappApi.sendMessage(conversationId, organizationId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: whatsappKeys.messages(conversationId, organizationId),
      });
      queryClient.invalidateQueries({ queryKey: whatsappKeys.conversations() });
    },
  });
}

export function useMarkConversationRead(conversationId: string, organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => whatsappApi.markAsRead(conversationId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.conversations() });
    },
  });
}

export function useWhatsAppWindow(
  conversationId: string | undefined,
  organizationId: string | undefined,
) {
  return useQuery({
    queryKey: whatsappKeys.window(conversationId ?? '', organizationId ?? ''),
    queryFn: () => whatsappApi.getWindowStatus(conversationId ?? '', organizationId ?? ''),
    // A rejected window lookup reports "closed", which tells the composer to
    // demand a template while the window is in fact open.
    enabled: !!conversationId && !!organizationId,
    refetchInterval: 30_000,
    staleTime: 30_000,
  });
}

export function useWhatsAppTemplates(organizationId: string | undefined) {
  return useQuery({
    queryKey: whatsappKeys.templates(organizationId ?? ''),
    queryFn: () => whatsappApi.getTemplates(organizationId ?? ''),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useSyncWhatsAppTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string) => whatsappApi.syncTemplates(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.all });
    },
  });
}

export function useWhatsAppStream(
  organizationId: string | undefined,
  getAccessToken?: () => Promise<string | null>,
) {
  const queryClient = useQueryClient();

  const onMessage = useCallback(
    (raw: string) => {
      try {
        const event: WhatsAppStreamEvent = JSON.parse(raw);
        if (event.type === 'new_message') {
          queryClient.invalidateQueries({
            queryKey: whatsappKeys.messages(event.conversationId, organizationId ?? ''),
          });
          queryClient.invalidateQueries({ queryKey: whatsappKeys.conversations() });
        } else if (event.type === 'status_update') {
          queryClient.invalidateQueries({
            queryKey: whatsappKeys.messages(event.conversationId, organizationId ?? ''),
          });
        }
      } catch {
        // ignore malformed events
      }
    },
    [queryClient],
  );

  return useSSEStream({
    path: 'communications/whatsapp/stream',
    organizationId,
    enabled: !!organizationId,
    getAccessToken,
    onMessage,
  });
}
